// src/components/Common/Action.tsx

import React, { useState } from "react";
import { 
  IconButton, 
  Menu, 
  MenuItem, 
  Tooltip, 
  ListItemIcon, 
  ListItemText,
  useTheme,
  alpha
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

export interface ActionItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  color?: string;
}

export interface ActionProps {
  actions: ActionItem[];
}

const Action: React.FC<ActionProps> = ({ actions }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title="Actions" arrow>
        <IconButton 
          onClick={handleOpen}
          sx={{ 
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            }
          }}
        >
          <MoreHorizIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: { 
              borderRadius: 2,
              minWidth: 180,
              mt: 1,
              overflow: 'visible',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              }
            }
          }
        }}
      >
        {actions.map((action, index) => (
          <MenuItem
            key={`action-${index}-${action.label}`}
            disabled={action.disabled}
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
              action.onClick();
            }}
            sx={{ 
              py: 1.5,
              px: 2.5,
              borderLeft: action.color ? `4px solid ${action.color}` : 'none',
              "&:hover": {
                bgcolor: action.color 
                  ? alpha(action.color, 0.05)
                  : alpha(theme.palette.primary.main, 0.05)
              }
            }}
          >
            {action.icon && (
              <ListItemIcon sx={{ color: action.color || 'inherit', minWidth: 36 }}>
                {action.icon}
              </ListItemIcon>
            )}
            <ListItemText 
              primary={action.label} 
              primaryTypographyProps={{ 
                fontWeight: 500,
                fontSize: '0.9rem'
              }} 
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default Action;
